import React, { useState, useEffect } from 'react';
import { MessageSquare, Home, Search, Plus, AlertCircle, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Testimonial, fetchApprovedTestimonials, createTestimonial, fetchUserTestimonials } from '../lib/testimonials';
import TestimonialCard from '../components/TestimonialCard';
import TestimonialForm from '../components/TestimonialForm';
import { supabase } from '../lib/supabase';
import AuthRequiredModal from '../components/AuthRequiredModal';

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [userTestimonials, setUserTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const navigate = useNavigate();
  
  const perPage = 10;

  useEffect(() => {
    checkAuthStatus();
    loadTestimonials();
  }, [currentPage, searchQuery]);

  useEffect(() => {
    if (isLoggedIn) {
      loadUserTestimonials();
    }
  }, [isLoggedIn]);

  async function checkAuthStatus() {
    const { data: { session } } = await supabase.auth.getSession();
    setIsLoggedIn(!!session);
  }

  async function loadTestimonials() {
    setLoading(true);
    try {
      const { data, count } = await fetchApprovedTestimonials({
        page: currentPage,
        perPage,
        searchQuery
      });
      
      setTestimonials(data);
      setTotalCount(count);
    } catch (error) {
      console.error('Error loading testimonials:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadUserTestimonials() {
    try {
      const userTestimonials = await fetchUserTestimonials();
      setUserTestimonials(userTestimonials);
    } catch (error) {
      console.error('Error loading user testimonials:', error);
    }
  }

  async function handleSubmitTestimonial(data: { title: string; content: string }) {
    try {
      setError('');
      setSuccess('');
      
      const testimonial = await createTestimonial(data);
      
      if (!testimonial) throw new Error('Erro ao criar testemunho');
      
      setSuccess('Testemunho enviado com sucesso! Ele será revisado antes de ser publicado.');
      setIsFormOpen(false);
      loadUserTestimonials();
    } catch (err: any) {
      setError(err.message || 'Erro ao enviar testemunho');
    }
  }

  function handleNewTestimonial() {
    if (!isLoggedIn) {
      setIsAuthModalOpen(true);
      return;
    }
    
    setIsFormOpen(true);
  }

  const totalPages = Math.ceil(totalCount / perPage);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <MessageSquare className="h-8 w-8 text-brand-teal" />
          <h1 className="text-3xl font-bold text-brand-teal">Testemunhos</h1>
        </div>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors"
        >
          <Home className="h-4 w-4" />
          <span>Voltar para Home</span>
        </button>
      </div>

      <div className="mb-8">
        <p className="text-gray-600 mb-4">
          Conheça histórias inspiradoras de como Deus tem transformado vidas em nossa comunidade. 
          Compartilhe também o seu testemunho e inspire outros com o que Deus tem feito em sua vida.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="relative w-full sm:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar testemunhos..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
            />
          </div>
          
          <button
            onClick={handleNewTestimonial}
            className="w-full sm:w-auto bg-brand-teal text-white px-6 py-2 rounded-lg hover:bg-brand-teal/90 transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="h-4 w-4" />
            <span>Compartilhar Testemunho</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg flex items-start">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 text-green-600 rounded-lg flex items-start">
          <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{success}</span>
        </div>
      )}

      {isFormOpen ? (
        <TestimonialForm
          onSubmit={handleSubmitTestimonial}
          onCancel={() => setIsFormOpen(false)}
        />
      ) : (
        <>
          {/* Testemunhos do usuário */}
          {isLoggedIn && userTestimonials.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Seus Testemunhos</h2>
              <div className="space-y-4">
                {userTestimonials.map((testimonial) => (
                  <div key={testimonial.id} className="bg-white p-4 rounded-xl shadow-sm">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-medium text-brand-teal">{testimonial.title}</h3>
                      <span className={`text-sm px-2 py-1 rounded-full ${
                        testimonial.status === 'aprovado'
                          ? 'bg-green-100 text-green-800'
                          : testimonial.status === 'rejeitado'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {testimonial.status === 'aprovado' ? 'Aprovado' : 
                         testimonial.status === 'rejeitado' ? 'Rejeitado' : 'Em análise'}
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm line-clamp-2">{testimonial.content}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      Enviado em {new Date(testimonial.created_at).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Lista de testemunhos aprovados */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal"></div>
            </div>
          ) : testimonials.length > 0 ? (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Testemunhos da Comunidade</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {testimonials.map((testimonial) => (
                  <TestimonialCard 
                    key={testimonial.id} 
                    testimonial={testimonial}
                    showAuthor={false}
                  />
                ))}
              </div>
              
              {/* Paginação */}
              {totalPages > 1 && (
                <div className="mt-8 flex justify-center">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 rounded-lg border border-gray-300 disabled:opacity-50"
                    >
                      Anterior
                    </button>
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-8 h-8 rounded-full ${
                          currentPage === page
                            ? 'bg-brand-teal text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 rounded-lg border border-gray-300 disabled:opacity-50"
                    >
                      Próxima
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl shadow-sm">
              <MessageSquare className="h-16 w-16 text-brand-teal/20 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-brand-teal mb-2">
                Nenhum testemunho encontrado
              </h2>
              <p className="text-gray-600 max-w-md mx-auto">
                {searchQuery
                  ? 'Não encontramos resultados para sua busca. Tente outros termos.'
                  : 'Seja o primeiro a compartilhar seu testemunho!'}
              </p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-4 px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors"
                >
                  Limpar busca
                </button>
              )}
            </div>
          )}
        </>
      )}

      {/* Modal de autenticação necessária */}
      <AuthRequiredModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        message="Para compartilhar seu testemunho, você precisa estar logado."
        returnTo="/testimonials"
      />
    </div>
  );
}